<script lang="ts">
    import vm, { JourneyNode, setLocalStorageItem } from '@journeys/Journeys.viewmodel';

    import HSLColors from '@shared/components/ColorPickers/HSLColorValues';

    // components
    import NodeTemplate from '@journeys/components/NodePartials/NodeTemplate.vue';
    import ItemGrid from '@shared/components/ItemGrid.vue';
    import { calc_snap, cancelFrame, deepClone, requestFrame } from '@shared/utils';
    import { vNotificationBadge, popoverInfo } from '@shared/utils/popovers';

    // assets
    import { TrashSVG } from '@shared/assets/vector/ui/IconsUI';

    // enums
    import { computed } from 'vue';
    import { NodeTaskVariation, NodeType } from '../../../../Enums';

    const component = {
        name: 'Partial_NodePresetList',
    };
    export default {
        name: component.name,
    };

    /*
$Resources.NodeList
    */
</script>

<script lang="ts" setup>
    const { editor, nav, settings, account, activeJourney } = vm,
        props = defineProps<{
            columns: number;
            gap?: number;
            list: Indexable<PropertyKey, JourneyNode & { runtime: { state: { disabled: boolean } } }>;
            editable?: boolean;
            cellAsp?: number;
        }>();

    let dragFrame = 0;
    const handleDragStart = (ev: PointerEvent, item: JourneyNode) => {
        document.body.classList.add('prevent-select');
        let evTarget = ev.target as HTMLElement,
            clone = evTarget.cloneNode(true) as HTMLElement,
            snap = settings.grid.snap,
            snap_spacing = settings.grid.spacing;

        clone.classList.add('clone');
        clone.style.position = 'absolute';
        clone.style.pointerEvents = 'none';
        clone.style.zIndex = '20';
        clone.style.transition = 'opacity .14s ease-out';
        clone.style.transitionProperty = 'opacity, height, width, top, left';
        clone.style.transitionDuration = '.8s .14s';
        clone.style.transformOrigin = 'top';

        let rect = evTarget.getBoundingClientRect(),
            o = {
                x: ev.x,
                y: ev.y,
                sx: 0,
                sy: 0,
                elOffsetX: ev.offsetX,
                elOffsetY: ev.offsetY,
                h: rect.height,
                w: rect.width,
                rect,
                el: clone,
            };

        if (snap) {
            let { x, y } = calc_snap({ x: ev.x, y: ev.y }, snap_spacing);
            o.sx = x;
            o.sy = y;
        }

        clone.style.height = o.h + 'px';
        clone.style.width = o.w + 'px';
        // clone.style.top = o.y + 'px'
        clone.style.left = o.rect.x + 'px';
        clone.style.top = o.rect.y + 'px';
        // clone.style.left = o.rect.x + 'px'

        evTarget.style.transform = 'scale(0)';

        document.body.appendChild(clone);

        let overCanvas = false,
            zscaled = computed(() => {
                let dist_mod = nav.p / (nav.p - nav.z),
                    spacing = settings.grid.spacing * dist_mod;
                // spacing = ~~(settings.grid.spacing * ((nav.p - nav.z) / nav.p) + .5)
                // spacing = settings.grid.spacing

                return {
                    dist_mod,
                    spacing,
                    item: {
                        h: item.h * dist_mod,
                        w: item.w * dist_mod,
                    },
                    rect: {
                        y: o.rect.y - (o.rect.y % spacing),
                        x: o.rect.x - (o.rect.x % spacing),
                    },
                };
            });

        const handleDrag = (ev: PointerEvent) => {
            cancelFrame(dragFrame);
            dragFrame = requestFrame(() => {
                let evTargetType = (ev.target as HTMLElement).dataset.nodetype,
                    { x, y } = { x: ev.x - o.x, y: ev.y - o.y };

                if (!overCanvas && /canvas/.test(evTargetType!)) {
                    let { item, spacing, rect, dist_mod } = zscaled.value;
                    overCanvas = true;

                    if (snap) clone.style.transitionProperty = 'transform, opacity, height, width, top, left';
                    clone.style.height = item.h + 'px';
                    clone.style.width = item.w + 'px';

                    clone.style.top = o.rect.y + o.elOffsetY - o.elOffsetY / (o.h / item.h) + 'px';
                    clone.style.left = o.rect.x + o.elOffsetX - o.elOffsetX / (o.w / item.w) + 'px';
                } else if (overCanvas && !/canvas/.test(evTargetType!)) {
                    overCanvas = false;

                    if (snap) clone.style.transitionProperty = 'opacity, height, width, top, left';
                    clone.style.top = o.y - o.elOffsetY + 'px';
                    clone.style.left = o.x - o.elOffsetX + 'px';
                    clone.style.height = o.h + 'px';
                    clone.style.width = o.w + 'px';
                }

                if (snap && overCanvas) {
                    let { spacing } = zscaled.value;

                    let s = calc_snap(
                        {
                            x: ev.x - o.x,
                            y: ev.y - o.y,
                        },
                        spacing
                    );
                    x = s.x;
                    y = s.y;
                }

                clone.style.transform = `translate3d(${x}px,${y}px,0) scale(1)`;
            });
        };

        document.addEventListener('pointermove', handleDrag);
        document.addEventListener(
            'pointerup',
            (ev: PointerEvent) => {
                document.body.classList.remove('prevent-select');

                evTarget.style.transform = '';
                clone.style.opacity = '0';
                clone.style.transition = 'opacity 0s ease';
                setTimeout(() => {
                    clone.remove();
                }, 1000);

                document.removeEventListener('pointermove', handleDrag);

                let evTargetType = (ev.target as HTMLElement).dataset.nodetype;

                // escape any chanses of creating multiple starting node
                // but also check if the starting node exists on canvas
                let currentStartingNode = activeJourney.startingNode;
                if (item.data.type === NodeType.Segment && currentStartingNode)
                    if (activeJourney.nodes[currentStartingNode]) return;
                    else activeJourney.startingNode = null;

                if (/canvas/.test(evTargetType!)) {
                    let newNode = deepClone<any>(item);

                    let cloneRect = clone.getBoundingClientRect();

                    newNode.x = ev.offsetX - nav.zscale_up(ev.x - cloneRect.x);
                    newNode.y = ev.offsetY - nav.zscale_up(ev.y - cloneRect.y);

                    // long version for clarity for now
                    // newNode.h = newNode.h
                    // newNode.w = newNode.w

                    // newNode.f = newNode.f
                    // newNode.fc = newNode.fc
                    // newNode.i = newNode.i
                    // newNode.ic = newNode.ic

                    editor.events.emit('newnode', {
                        vd: newNode,
                        data: newNode.data,
                    });
                }
            },
            { once: true }
        );
    };

    const deleteFromList = (id: string) => {
        delete account.nodePresets[id];
        setLocalStorageItem('nodePresets', account.nodePresets, true, {
            encodeTarget: 'json',
            jsonWhiteSpace: 0,
        });
    };

    const filteredList = computed(() => {
        let list = Object.values(props.list).reduce((acc: any, item: any) => {
            //if (item.data.var === NodeTaskVariation.Sms && !globalMethods.hasFeature('BackOffice')) return acc
            acc.push(item);
            return acc;
        }, []);

        return list;
    });
</script>

<template>
    <div class="section-group node-list">
        <ItemGrid :columns="columns" :gap="gap || 6" style="row-gap: 2.2rem" :asp="cellAsp">
            <transition-group name="list">
                <div
                    class="pos-r"
                    v-for="item in filteredList"
                    :key="item.id"
                    :id="'NodePreset' + item.id"
                    v-notification-badge:warning="{
                        classes: 'cursor-ptr ttt',
                        show: item.data.type === NodeType.Segment && !!vm.activeJourney.startingNode,
                        event: {
                            type: 'click',
                            handler: (ev: MouseEvent) =>
                                popoverInfo(
                                    ev.target as HTMLElement,
                                    $Resources.SingleStartingSegmentWarningHead,
                                    '',
                                    $Resources.SingleStartingSegmentWarningBody
                                ),
                        },
                    }">
                    <div class="item-cell" :class="item.data.type === NodeType.Segment && !!vm.activeJourney.startingNode && 'blocked'">
                        <NodeTemplate
                            :id="item.id"
                            :frame="item.f"
                            :frameColor="(HSLColors as Indexable<number, any>)[item.fc]"
                            :icon="item.i"
                            :iconColor="(HSLColors as Indexable<number, any>)[item.ic]"
                            :height="item.h"
                            :width="item.w"
                            :type="item.data.type"
                            @pointerdown.left="(ev) => handleDragStart(ev, item)" />
                        <span class="item-cell-name">{{ item.data.name }}</span>
                        <div v-if="editable" class="delete-button" @click="() => deleteFromList(item.data.title)">
                            <TrashSVG fill="#fff" />
                        </div>
                    </div>
                </div>
            </transition-group>
        </ItemGrid>
    </div>
</template>

<style>
    .item-cell {
        position: relative;
        user-select: none;
        height: 100%;
        width: 100%;
    }

    .item-cell.blocked {
        pointer-events: none;
        opacity: 0.5;
    }

    .item-cell-name {
        display: block;
        margin: 0.625rem 0;
        font-size: smaller;
        font-weight: 600;
        text-align: center;
        pointer-events: none;
        white-space: nowrap;
    }

    .node-list .item-cell.list-move,
    .node-list .item-cell.list-enter-active,
    .node-list .item-cell.list-leave-active {
        transition: all 0.3s ease;
    }

    /* .node-list .item-cell.list-move {
   transition-duration: 1.5s;
} */

    .node-list .item-cell.list-enter-from,
    .node-list .item-cell.list-leave-to {
        opacity: 0;
        transform: scale(0.3);
        z-index: -1;
        transition-delay: 0.2s;
    }

    .node-list .item-cell.list-leave-active {
        position: absolute;
    }

    .delete-button {
        display: flex;
        position: absolute;
        height: 1.2rem;
        width: 1.2rem;
        top: 0.1rem;
        right: 0.1rem;
        padding: 0.25rem;
        border-radius: 1.2rem;
        background-color: rgb(187, 70, 70);
        outline: 0.15rem solid rgba(208, 240, 255, 0.5);
        cursor: pointer;
        transition: transform 0.1s ease;
        transition-property: transform, opacity;
        transform: scale(0.2);
        opacity: 0;
        pointer-events: none;
    }

    .delete-button svg {
        fill: #fff;
    }

    .item-cell:hover .delete-button {
        transform: scale(1);
        opacity: 1;
        pointer-events: all;
    }

    .item-cell .delete-button:hover {
        transform: scale(1.2);
    }

    .node-list .node-wrapper {
        display: flex;
        justify-content: center;
        width: 100%;
        height: 100%;
        cursor: grab;

        transition: transform 0.1s var(--ttf-eager);
    }

    body:has(.node-wrapper.clone) {
        cursor: grabbing;
    }

    .clone.node-wrapper {
        left: 10%;
    }

    .node-list .item-cell {
        padding: 0.4rem;
        background-color: #f5f6f7;
        border-radius: 4px;
        box-shadow: 0 0.2rem 0.8rem -0.3rem #00000014;
        border: 1px solid #d5d5d5;
        outline: 0px solid transparent;

        transition-property: border-color, box-shadow, outline;
        transition-duration: 0.3s;
        transition-timing-function: ease;
    }

    .node-list .item-cell:hover {
        box-shadow: 0 6px 18px -6px #00000054;
        border-color: #1fa9b0;
        border: 1px solid rgb(31 169 176);
        outline: 1px solid #1fa9b0;
    }

    .editor-busy .property-window {
        user-select: none;
    }
</style>
